import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ShoppingBag, ArrowRight, ShieldCheck, Sparkles, Truck, CheckCircle2 } from 'lucide-react';

const categoryMeta = {
  all: { title: 'All Spices', note: 'Complete range for daily and professional kitchens' },
  powder: { title: 'Powders', note: 'Fine-ground essentials for consistent flavor' },
  blend: { title: 'Blends', note: 'Balanced masalas crafted for signature dishes' },
  whole: { title: 'Whole Spices', note: 'Aroma-rich raw spices for deep flavor extraction' },
};

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, [filter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?category=${filter}` : '';
      const response = await api.get(`/products${params}`);
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product, variant) => {
    try {
      await addToCart(variant.id, product.id, 1);
    } catch (error) {
      toast.error('Failed to add item to cart');
    }
  };

  const categories = [
    { id: 'all', label: 'All Spices' },
    { id: 'powder', label: 'Powders' },
    { id: 'blend', label: 'Blends' },
    { id: 'whole', label: 'Whole Spices' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-sm font-medium border border-amber-300 mb-4">
            <Sparkles className="h-4 w-4" />
            Curated Product Catalogue
          </span>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent mb-5">
            Premium Spices Collection
          </h1>
          <p className="text-lg text-amber-800 max-w-3xl mx-auto">
            Quality-focused spices for households, chefs, and food businesses. Built for consistency, freshness, and authentic taste.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <div className="bg-white border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-amber-950">100%</p>
            <p className="text-xs text-amber-700">Purity Focus</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-amber-950">599+</p>
            <p className="text-xs text-amber-700">Free Shipping</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-amber-950">Small Lot</p>
            <p className="text-xs text-amber-700">Batch Handling</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-amber-950">Secure</p>
            <p className="text-xs text-amber-700">Checkout Flow</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mb-5">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setFilter(category.id)}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                filter === category.id
                  ? 'bg-amber-700 text-white shadow-lg'
                  : 'bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-amber-700 mb-10">
          <span className="font-semibold text-amber-900">{categoryMeta[filter].title}:</span> {categoryMeta[filter].note}
        </p>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="group border border-amber-200/80 hover:border-amber-400/90 hover:shadow-xl transition-all duration-500 bg-white overflow-hidden rounded-2xl"
              >
                <Link to={`/product/${product.slug}`}>
                  <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-amber-100/60 via-amber-50 to-orange-100/50">
                    <div className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur-sm text-amber-900 text-xs font-semibold px-3 py-1 rounded-full border border-amber-200">
                      Premium Quality
                    </div>
                    <img
                      src={product.image_urls?.[0] || '/placeholder-product.png'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>

                <div className="p-6">
                  <Link to={`/product/${product.slug}`}>
                    <h3 className="text-xl font-bold text-amber-950 group-hover:text-amber-700 transition-colors leading-tight min-h-[56px]">
                      {product.name}
                    </h3>
                  </Link>

                  {product.variants && product.variants.length > 0 && (
                    <div className="space-y-4 mt-4">
                      <div className="bg-amber-50/70 border border-amber-100 rounded-xl p-3">
                        <div className="flex items-end justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Starting Price</p>
                            <p className="text-2xl font-bold text-amber-950">
                              Rs {Math.min(...product.variants.map((v) => Number(v.selling_price || 0))).toFixed(2)}
                            </p>
                          </div>
                          <p className="text-xs text-amber-700 text-right">
                            {product.variants.length} pack options
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => handleAddToCart(product, product.variants[0])}
                        className="w-full bg-amber-700 hover:bg-amber-800 text-white px-5 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <ShoppingBag className="w-5 h-5" />
                        Add to Cart
                        <ArrowRight className="w-4 h-4" />
                      </button>

                      <div className="flex items-center gap-2 text-xs text-amber-800">
                        <ShieldCheck className="w-4 h-4 text-green-700" />
                        <span>Fresh stock. Quality checked.</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20 bg-white border border-amber-200 rounded-2xl">
            <p className="text-amber-700 text-lg">No products found in this category.</p>
          </div>
        )}

        <section className="mt-16 bg-white border border-amber-200 rounded-2xl p-6 md:p-8">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="flex items-start gap-3">
              <Truck className="h-5 w-5 text-amber-700 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-950">Reliable Dispatch</p>
                <p className="text-sm text-amber-700">Orders are packed and dispatched with freshness-safe handling.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-amber-700 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-950">Transparent Pricing</p>
                <p className="text-sm text-amber-700">Clear tax-aware pricing and invoice-backed order records.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-700 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-950">Trusted Quality</p>
                <p className="text-sm text-amber-700">Quality-checked products for daily and professional cooking standards.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Shop;
